import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, FlaskConical, Gauge, FileCheck2 } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { LAB_TESTS } from "../data/content";

export default function LabTest() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const test = LAB_TESTS.find((x) => x.slug === slug);
  if (!test) return <Navigate to="/qualitaet" replace />;
  const card = lang === "de" ? test.de : test.en;
  const d = lang === "de" ? test.detail.de : test.detail.en;

  return (
    <>
      <Seo
        title={`${card.t} – Materialprüfung | Qualität | PlastoNord`}
        description={d.lead}
        path={`/qualitaet/${slug}`}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: t("nav.quality"), path: "/qualitaet" }, { name: card.t, path: `/qualitaet/${slug}` }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: `${card.t} – Materialprüfung`, description: d.lead, about: card.t }}
      />
      <section className="border-b border-[#e4e9f0] bg-[#f6f8fb] grain">
        <div className="pn-container py-14 md:py-20">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: t("nav.quality"), path: "/qualitaet" }, { name: card.t, path: `/qualitaet/${slug}` }]} />
          <Reveal>
            <p className="overline">{lang === "de" ? "Materialprüfung" : "Material test"} · {test.detail.norm}</p>
            <h1 className="mt-4 max-w-4xl font-head text-4xl font-light leading-[1.08] tracking-tight text-[#16181D] md:text-5xl">{card.t}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#4a4d52]">{d.lead}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/kontakt" className="btn-primary" data-testid="labtest-request">{t("cta.request")} <ArrowRight size={16} /></Link>
              <Link to="/qualitaet" className="btn-ghost">{lang === "de" ? "Alle Prüfungen" : "All tests"}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="pn-container grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="pn-card h-full p-8">
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><Gauge size={20} /></span>
              <h2 className="mt-4 font-head text-xl font-semibold text-[#16181D]">{lang === "de" ? "Was wird gemessen?" : "What is measured?"}</h2>
              <ul className="mt-4 space-y-3">
                {d.measures.map((m) => (
                  <li key={m} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={17} className="mt-0.5 shrink-0 text-[#E2611C]" /> {m}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="pn-card h-full p-8">
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><FileCheck2 size={20} /></span>
              <h2 className="mt-4 font-head text-xl font-semibold text-[#16181D]">{lang === "de" ? "Warum ist diese Prüfung wichtig?" : "Why this test matters"}</h2>
              <ul className="mt-4 space-y-3">
                {d.why.map((w) => (
                  <li key={w} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={17} className="mt-0.5 shrink-0 text-[#E2611C]" /> {w}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <div className="pn-container mt-6">
          <div className="rounded-xl bg-[#16181D] p-5 text-sm text-white/80">
            <span className="font-medium text-white">{lang === "de" ? "Norm / Methode" : "Standard / method"}:</span> {test.detail.norm} · {lang === "de" ? "Durchführung im eigenen Labor von PlastoNord." : "Performed in PlastoNord's in-house laboratory."}
          </div>
        </div>
      </section>

      {/* other tests */}
      <section className="bg-[#f6f8fb] py-16">
        <div className="pn-container">
          <h2 className="font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "Weitere Prüfungen" : "More tests"}</h2>
          <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {LAB_TESTS.filter((x) => x.slug !== slug).map((x) => (
              <motion.div key={x.slug} variants={itemVariant}>
                <Link to={`/qualitaet/${x.slug}`} className="pn-card flex items-center justify-between p-5" data-testid={`labtest-link-${x.slug}`}>
                  <span className="font-head font-medium text-[#16181D]">{(lang === "de" ? x.de : x.en).t}</span>
                  <ArrowRight size={16} className="text-[#E2611C]" />
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
